import { useMemo } from 'react'

import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import { getDayKeyInTimezone } from '@/components/integration/nylas/scheduler/helpers'
import useSchedulerDays, {
  SchedulerDay,
} from '@/components/integration/nylas/scheduler/hooks/useSchedulerDays'
import availabilityService from '@/lib/services/availabilityService'

/**
 * Returns the `SchedulerDay` whose local day matches `selectedDate`,
 * or `null` if there is no selection or no match. Reads `selectedDate`,
 * `selectedDateRange` and `timezone` from the scheduler contexts and
 * relies on React Query caching so this is cheap to call alongside any
 * sibling `useSchedulerDays` consumer.
 */
export default function useSelectedSchedulerDay(): SchedulerDay | null {
  const { timezone, owningEntityType, owningEntityId, availabilitySource } =
    useSchedulerConfig()
  const {
    selectedDate,
    selectedDateRange,
    isExtendedAvailabilityReady,
    availabilityFilter,
  } = useSchedulerSelection()
  const { schedulerDays: primaryDays } = useSchedulerDays(
    owningEntityType,
    owningEntityId,
    selectedDateRange.start,
    selectedDateRange.end,
    timezone,
    availabilityFilter,
    { availabilitySource },
  )

  const extendedAvailabilityRange = useMemo(
    () => availabilityService.getExtendedAvailabilityDateRange(),
    [],
  )

  const { schedulerDays: extendedDays } = useSchedulerDays(
    owningEntityType,
    owningEntityId,
    extendedAvailabilityRange.start,
    extendedAvailabilityRange.end,
    timezone,
    availabilityFilter,
    { enabled: isExtendedAvailabilityReady, availabilitySource },
  )

  return useMemo(() => {
    if (!selectedDate) {
      return null
    }

    const candidates = [...(primaryDays ?? []), ...(extendedDays ?? [])]
    if (candidates.length === 0) {
      return null
    }

    const selectedDateKey = getDayKeyInTimezone(selectedDate, timezone)

    return (
      candidates.find(
        (candidate) =>
          getDayKeyInTimezone(candidate.date, timezone) === selectedDateKey,
      ) ?? null
    )
  }, [primaryDays, extendedDays, selectedDate, timezone])
}
